import React, { useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useHistory } from "react-router-dom";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import Loading from "Components/Loading";
import DeleteAccountButton from "Pages/MyAccounts/DeleteAccountButton";
import useSession from "Utils/Hooks/useSession";
import errorIcon from "Assets/Icons/error.svg";

import "./BankList.scss";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import useTransactionInitiated from "Utils/Hooks/useTransactionInitiated";
// eslint-disable-next-line no-unused-vars
import _, { set } from "lodash";
import { FUND_SOURCE_TYPE, PRODUCTTYPES, Routes } from "Utils/Constants";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import { faro } from "@grafana/faro-react";

const BankList = () => {
  const fundType = useFundType();
  const { session } = useSession();
  const mode = useMode();

  const [fundingSources, setFundingSources] = useState(undefined);
  const [bankAccountList, setBankAccountList] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [userSelection, setUserSelection] = useState(null);

  const vipApi = useVipApiFetcher();
  const history = useHistory();
  const { transactionInitiated } = useTransactionInitiated();
  const isDepositMode = mode === MODE.deposit;
  const [sessionStorageTransAmount, setSessionStorageTransAmount] =
    useSessionStorage("vip-transaction-amount");

  useEffect(() => {
    if (!vipApi.ready) return;
    let mounted = true;

    (async () => {
      let fundList = [];
      let plaidFundList = [];

      fundList = await vipApi.fetchData("/api/bank/ach");
      setBankAccountList(fundList);
      setFundingSources(fundList);
      if (session?.patronProductType === PRODUCTTYPES.ONLINE) {
        plaidFundList = fundList.filter(
          (fund) =>
            fund.achOpenType === FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
            fund.achOpenType === FUND_SOURCE_TYPE.FINICITY
        ); // Get only Plaid accounts
        setFundingSources(plaidFundList);
      }

      if (!mounted) return;

      setIsLoading(false);
    })();
    return () => {
      mounted = false;
    }; // Unmount func
  }, [vipApi.ready]);

  if (isLoading) return <Loading />;

  async function updateAccount(account) {
    if (account) {
      faro.api.pushEvent(
        `User set default bank: ${account.maskedAccountNumber}`
      );
      const body = {
        bankName: account.bankName,
        routingNumber: account.routingNumber,
        accountNumber: account.accountNumber,
        maskedAccountNumber: account.maskedAccountNumber,
        achOpenType: account.achOpenType,
        ipAddress: window.sessionStorage.getItem("clientIpAddress"),
      };
      const customErrorButtons =
        mode === MODE.manageBanks && transactionInitiated
          ? [
              {
                label: "TRY AGAIN",
                route: Routes.MY_ACCOUNTS,
              },
            ]
          : transactionInitiated
            ? [
                {
                  label: "TRY AGAIN",
                  route: Routes.MY_ACCOUNTS,
                },
                {
                  label: `COMPLETE ${isDepositMode ? "DEPOSIT" : "WITHDRAW"}`,
                  route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
                },
              ]
            : undefined;
      const response = await vipApi.fetch("/api/bank/update", "POST", body);
      if (response) {
        switch (response.status) {
          case 200:
            setUserSelection(account.accountNumber);
            break;
          case 401:
            console.error(response);
            faro.api.pushError(new Error("Bank Update 401 Error"), {
              context: response,
            });
            history.push("/expired");
            break;
          default:
            console.error(response);
            const errorDetails = await response.json();
            faro.api.pushError(new Error("Bank Update Error"), {
              context: errorDetails,
            });
            history.push({
              pathname: "/error",
              state: {
                message:
                  errorDetails.patronErrorMessage ??
                  "Error Updating Funding Source",
                showButton: true,
                allowRetry: false,
                customButtons: customErrorButtons,
                transactionInitiated: false,
              },
            });
        }
      }
    }
  }

  let defaultAccountSet =
    !fundingSources?.filter(
      (account) =>
        account.defaultAccountNumber === "True" &&
        session.transactionAmount > account.availableLimit
    ).length > 0;

  const fundList = fundingSources?.map((account, index) => {
    const isOnline = session?.patronProductType === PRODUCTTYPES.ONLINE;
    const accountAvailable =
      (isOnline &&
        isDepositMode &&
        sessionStorageTransAmount < account.availableLimit) ||
      !isOnline ||
      !isDepositMode;
    let accountIsDefault = false;
    accountIsDefault = account.defaultAccountNumber === "True";
    // for VIPO only change default account if default has insufficient funds
    if (isOnline && !defaultAccountSet) {
      if (account.defaultAccountNumber === "True" && accountAvailable) {
        accountIsDefault = true;
        defaultAccountSet = true;
      } else if (account.defaultAccountNumber === "True" && !accountAvailable) {
        accountIsDefault = false;
        defaultAccountSet = false;
      } else if (accountAvailable) {
        accountIsDefault = true;
        defaultAccountSet = true;
        updateAccount(account);
      } else {
        accountIsDefault = false;
      }
    }
    return (
      <Row
        key={index}
        className={index === 0 ? "border-top border-bottom" : "border-bottom"}
      >
        <Col md={1} xs={1} className="bank-name">
          <input
            type="radio"
            disabled={!accountAvailable}
            value={account.accountNumber}
            className="click-set-default-account"
            checked={
              userSelection
                ? userSelection === account.accountNumber
                : accountIsDefault
            }
            onChange={() => updateAccount(account)}
          />
        </Col>
        <Col
          md={8}
          xs={7}
          className={accountAvailable ? "bank-name" : "bank-name-disabled"}
        >
          {account.bankName?.toUpperCase()}
        </Col>
        <Col
          md={2}
          xs={2}
          className={
            accountAvailable ? "account-number" : "account-number-disabled"
          }
        >
          X{account.accountNumberDisplay}
        </Col>
        <Col md={1} xs={2}>
          <DeleteAccountButton
            account={account}
            bankAccountData={fundingSources}
          />
        </Col>
        {!accountAvailable && (
          <Row className="warning-row">
            <Col className="warning-message" md={1} xs={1}>
              <img
                height="20"
                alt="Insufficient Funds in Account"
                src={errorIcon}
              />
            </Col>
            <Col className="warning-message" md={11} xs={11}>
              This account has insufficient funds. Bank accounts must have a
              higher balance than your deposit amount to complete a transaction
              with VIP Online.
            </Col>
          </Row>
        )}
      </Row>
    );
  });

  const onClickAdd = () => {
    history.push({ pathname: "/addAccount", state: { notRedirect: true } });
    faro.api.pushEvent(
      "User clicked to Add Account from My Funding Sources page"
    );
  };

  let button = (
    <button className="submit-button click-add-account" onClick={onClickAdd}>
      ADD ACCOUNT
    </button>
  );

  const tooManyBanks =
    fundType === FUNDTYPE.netspend
      ? bankAccountList?.length >= 3
      : bankAccountList?.length >= 4;
  if (tooManyBanks) {
    button = (
      <button className="submit-button disabled">CANNOT ADD ACCOUNT</button>
    );
  }

  return (
    <Container className="bank-list">
      {fundList}
      <Row className="button-row">
        <Col md={{ span: 6, offset: 3 }}>{!tooManyBanks && button}</Col>
        {tooManyBanks && (
          <Col sm={{ span: 10, offset: 1 }} xs={12}>
            <div className="invalid-feedback d-block text-center">
              {fundType === FUNDTYPE.netspend
                ? "Only 3 Prepaid accounts allowed. You may remove a Prepaid account to add a new one."
                : "Only 4 bank accounts allowed. You may remove a bank account to add a new one."}
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default BankList;
